import { coverFileFromVideo } from "@/lib/videoCover"
import { useEffect, useRef, useState } from "react"

type VideoCoverPickerProps = {
  src: string
  poster?: string
  initialTime?: number
  saveLabel?: string
  onSave?: (file: File | null, time: number) => Promise<void> | void
  onTimeChange?: (time: number) => void
  videoRef?: React.RefObject<HTMLVideoElement | null>
}

export default function VideoCoverPicker({
  src,
  poster,
  initialTime = 0,
  saveLabel,
  onSave,
  onTimeChange,
  videoRef,
}: VideoCoverPickerProps) {
  const localRef = useRef<HTMLVideoElement>(null)
  const [duration, setDuration] = useState(0)
  const [coverTime, setCoverTime] = useState(initialTime)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (videoRef) videoRef.current = localRef.current
  })

  useEffect(() => {
    setReady(false)
    setError("")
    setDuration(0)
    setCoverTime(initialTime)
  }, [src])

  const syncFrame = (el: HTMLVideoElement, time: number) => {
    el.pause()
    el.muted = true
    el.playsInline = true
    const len = Number.isFinite(el.duration) ? el.duration : 0
    const next = len > 0 ? Math.min(Math.max(time, 0), Math.max(len - 0.05, 0)) : Math.max(time, 0)
    try {
      if (Math.abs(el.currentTime - next) > 0.03) el.currentTime = next
    } catch {
      /* Some browsers reject seeks until metadata is ready. */
    }
  }

  const markReady = () => {
    const el = localRef.current
    if (!el) return
    const len = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : 0
    if (len > 0) setDuration(len)
    if (el.videoWidth || el.readyState >= 2) {
      setReady(true)
      syncFrame(el, coverTime)
    }
  }

  const setTime = (time: number) => {
    setCoverTime(time)
    onTimeChange?.(time)
    const el = localRef.current
    if (el) syncFrame(el, time)
  }

  const save = async () => {
    const el = localRef.current
    if (!el || !onSave) return
    setBusy(true)
    setError("")
    try {
      let frame: File | null = null
      try {
        frame = await coverFileFromVideo(el, coverTime)
      } catch (err) {
        console.error("Cover still export failed:", err)
      }
      await onSave(frame, coverTime)
      setError(
        frame
          ? "Cover saved."
          : "Cover frame saved. This browser blocked exporting a still, so the paused video frame is used instead.",
      )
    } catch (err) {
      console.error("Cover save failed:", err)
      setError((err as Error)?.message || "Could not save this cover. Try again.")
    } finally {
      setBusy(false)
    }
  }

  const helper = ready ? "Cover still · drag the slider to change the frame" : "Loading cover preview..."

  return (
    <div className="space-y-2 text-xs text-[#8A8A8A]">
      <label className="block">
        Cover frame {coverTime.toFixed(1)}s
        <input
          type="range"
          min={0}
          max={Math.max(duration, 1)}
          step={0.1}
          value={Math.min(coverTime, Math.max(duration, 1))}
          onInput={(event) => setTime(Number((event.target as HTMLInputElement).value))}
          onChange={(event) => setTime(Number(event.target.value))}
          className="mt-1 w-full"
        />
      </label>
      <div className="overflow-hidden rounded-xl border border-[#1F1F1F] bg-black">
        <video
          key={src}
          ref={localRef}
          src={src || undefined}
          poster={poster || undefined}
          muted
          playsInline
          preload="auto"
          controls={false}
          className="mx-auto h-56 w-full bg-black object-contain"
          onLoadedMetadata={markReady}
          onLoadedData={markReady}
          onCanPlay={markReady}
          onSeeked={() => setReady(true)}
          onError={() => setError("Could not load this clip for cover selection.")}
        />
        <p className="px-3 py-2 text-center text-[11px] text-[#8A8A8A]">{helper}</p>
      </div>
      {onSave ? (
        <button
          type="button"
          disabled={busy || !ready}
          onClick={() => void save()}
          className="w-full rounded-lg bg-[#00FF66] py-2 text-xs font-semibold text-black disabled:opacity-60"
        >
          {busy ? "Saving cover..." : saveLabel || "Save cover"}
        </button>
      ) : null}
      {error ? (
        <p className={`text-[11px] ${/saved/i.test(error) ? "text-[#00C805]" : "text-[#ff6b6b]"}`}>{error}</p>
      ) : null}
    </div>
  )
}
